import { Box, Stack, Typography, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import pdfIcon from './../../../../../assets/svg/attachmentPdfIcon.svg';
import AttachmentPopUp from './attachmentPopup';

import downloadIcon from '../../../../../assets/svg/downloadIcon.svg';
import otherFileTypeIcon from '../../../../../assets/svg/otherFileType.svg';
import videoIcon from '../../../../../assets/svg/videoIcon.svg';
import {
  axiosInstanceForPublicAPI,
  axiosInstanceForTenantAPI,
} from '../../../../../assets/utils/axiosInstance';
import {
  PDF_FILE_TYPE,
  DOC_FILE_TYPES,
  IMAGE_FILE_TYPES,
  VIDEO_FILE_TYPES,
} from '../../../../../constants/fileTypeConstants';
import {
  PDF_MIME_TYPE,
  DOC_MIME_TYPE,
  IMAGE_MIME_TYPES,
  VIDEO_MIME_TYPES,
} from '../../../../../constants/mimeTypeConstants';
import { useAuth } from '../../../../../contexts/auth/authContext';
import { useToastNotificationContext } from '../../../../../hooks/global/alert/useToastNotification';
import { AttachmentObject } from '../../../../../interfaces/AttachmentObject';
import ErrorType from '../../../../../interfaces/ErrorType';
import SnackBarErrorResponse from '../../../../common/snackBarErrorResponse';

const AttachedItem = ({
  id,
  url,
  fileType,
  name,
  downloadAttachmentsFlag,
}: AttachmentObject) => {
  const { reportId } = useParams();
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [error, setError] = useState<ErrorType>({
    errorType: false,
    message: '',
  });
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();

  const { slug } = useAuth();
  const [companySlug, setCompanySlug] = useState<string | null>(slug);

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
  };

  const addAuditLog = async () => {
    try {
      setError({
        errorType: false,
        message: '',
      });
      const newdata = {
        activity: `${name}`,
      };
      const response = await axiosInstanceForTenantAPI(companySlug).post(
        `/report/create/download-audit-log/${reportId}`,
        newdata,
      );
      setIsLoading(false);
      const res = response.data;
      return res;
    } catch (_) {
      setIsLoading(false);
    }
  };

  const downloadAttachment = async () => {
    if (downloadAttachmentsFlag) {
      setIsLoading(true);
      axiosInstanceForPublicAPI()
        .get(url, { responseType: 'blob' })
        .then((response) => {
          // Create blob link to download
          const downloadUrl = window.URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.setAttribute('download', name);
          // Append to html link element page
          document.body.appendChild(link);
          // Start download
          link.click();
          // Clean up and remove the link
          if (link.parentNode) link.parentNode.removeChild(link);
          if (reportId) addAuditLog();
          else setIsLoading(false);
          createToastNotification(
            'Attachment downloaded successfully.',
            'success',
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'You are not permitted to download any attachments files on this report.',
        }),
        'info',
      );
    }
  };

  const getIcon = (currfileType: string) => {
    if (IMAGE_FILE_TYPES.includes(currfileType)) return url;
    if (IMAGE_MIME_TYPES.includes(currfileType)) return url;
    if (VIDEO_FILE_TYPES.includes(currfileType)) return videoIcon;
    if (VIDEO_MIME_TYPES.includes(currfileType)) return videoIcon;
    if (DOC_FILE_TYPES.includes(currfileType)) return otherFileTypeIcon;
    if (DOC_MIME_TYPE === currfileType) return otherFileTypeIcon;
    if (PDF_FILE_TYPE === currfileType) return pdfIcon;
    if (PDF_MIME_TYPE === currfileType) return pdfIcon;

    return otherFileTypeIcon;
  };

  const openAttachment = () => {
    setOpenModal(true);
  };

  const handleCloseSnackbar = (reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setError((prevError) => ({ ...prevError, errorType: false }));
  };

  return (
    <Stack
      key={id}
      alignItems='center'
      spacing={1}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      sx={{
        position: 'relative',
        '&:hover': {
          bgcolor: '#00000016',
          borderRadius: '6px',
        },
      }}
    >
      <SnackBarErrorResponse
        open={error.errorType}
        message={error.message}
        handleCloseSnackbar={handleCloseSnackbar}
        severity={error.severity ? error.severity : 'error'}
      />

      <Box
        component='img'
        src={getIcon(fileType)}
        onClick={openAttachment}
        height={100}
        sx={{
          maxWidth: '100%',
          '&:hover': {
            cursor: 'pointer',
          },
        }}
      />
      <Typography
        sx={{
          overflowWrap: 'anywhere',
          padding: '10px',
          textAlign: 'center',
        }}
      >
        {name}
      </Typography>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: '20px',
            left: '40%',
            width: '32px',
            height: '32px',
            borderRadius: '10px',
            bgcolor: 'white',
            padding: 0.5,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {!isLoading && !error.errorType && isHovering && (
        <Box
          sx={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            width: '32px',
            height: '32px',
            borderRadius: '10px',
            bgcolor: 'white',
            padding: 0.5,
            transform: 'translate(-50%, 0%)',
            '&:hover': {
              bgcolor: 'grey',
              borderRadius: '6px',
              cursor: 'pointer',
            },
          }}
          component='img'
          src={downloadIcon}
          onClick={downloadAttachment}
        ></Box>
      )}
      {openModal && (
        <AttachmentPopUp
          handleClose={() => setOpenModal(false)}
          download={downloadAttachment}
          fileType={fileType}
          url={url}
          fileName={name}
        />
      )}
    </Stack>
  );
};

export default AttachedItem;
